import React from 'react'
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import api from '../../../axios/api';
import { Card, TextField, CardContent } from '@mui/material';
import CustomButton from '../../../componentsII/Buttons';
import { showNotification } from '../../../redux/slices/notificationSlice';
import { FormControl, MenuItem, Select } from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AddUpdateVehicleEmissionFactorMaster = ({ onClose, refetch, EmissionFactorId = null, initialData = null }) => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  const [fuel, setFuel] = useState([]);
  const [units, setUnits] = useState([]);
  const [vehicleType, setVehicleType] = useState([]);
  const [vehicleSubType, setVehicleSubType] = useState([]);
  const [filteredVehicleSubTypes, setFilteredVehicleSubTypes] = useState([]);
  
  const [selectedFuelForm, setSelectedFuelForm] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    vehicleTypeId: initialData?.vehicle_type_id || '',
    vehicleSubTypeId: initialData?.vehicle_subtype_id || '',
    fuelId: initialData?.fuel_id || '',
    unitId: initialData?.unit_id || '',
    emission: initialData?.emission_kgco2e || '',
    year: initialData?.valid_year ? new Date(initialData.valid_year, 0) : new Date(new Date().getFullYear(), 0),
  });

  const validate = (values) => {
    const newErrors = {};

    // Fuel Type validation
    if (!values.fuelId) {
      newErrors.fuelId = 'Fuel Type is required';
    }

    // Unit validation
    if (!values.unitId) {
      newErrors.unitId = 'Unit is required';
    }

    // // Vehicle Type validation
    // if (!values.vehicleTypeId) {
    //   newErrors.vehicleTypeId = 'Vehicle Type is required';
    // }

    // // Vehicle Sub Type validation
    // if (!values.vehicleSubTypeId) {
    //   newErrors.vehicleSubTypeId = 'Vehicle Sub Type is required';
    // }

    // Emission validation
    if (!values.emission) {
      newErrors.emission = 'Emission value is required';
    } else if (isNaN(values.emission)) {
      newErrors.emission = 'Emission must be a number';
    } else if (Number(values.emission) < 0) {
      newErrors.emission = 'Emission cannot be negative';
    }

    // Year validation
    if (!values.year) {
      newErrors.year = 'Year is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        id: initialData?.id || 0,
        vehicleTypeId: formData.vehicleTypeId,
        vehicleSubTypeId: formData.vehicleSubTypeId,
        fuelId: formData.fuelId,
        unitId: formData.unitId,
        emission: formData.emission,
        year: formData.year ? formData.year.getFullYear() : null
      };

      const response = await api.post('/admin/CreateUpdateVehicleFuelEmission', payload);
      if (response.data.status == '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch?.();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (error) {
      dispatch(showNotification({
        message: error.response.data.message,
        type: "ERROR"
      }));
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // When vehicle type changes, filter the subtypes and reset the subtype selection
    if (name === 'vehicleTypeId') {
      filterVehicleSubTypes(value);
      setFormData(prev => ({
        ...prev,
        vehicleSubTypeId: '' // Reset the subtype when type changes
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Filter vehicle subtypes based on selected vehicle type
  const filterVehicleSubTypes = (typeId) => {
    if (!typeId) {
      setFilteredVehicleSubTypes([]);
      return;
    }
    
    const filtered = vehicleSubType.filter(subType => subType.type_id === parseInt(typeId));
    setFilteredVehicleSubTypes(filtered);
  };

  const getFuel = async () => {
    try {
      const response = await api.get('/vehicle/getAllFuel?fuel_form=LIQUID&fuel_form=GAS&fuel_form=MASS');
      if (response.status == '200') {
        setFuel(response.data.data);
        
        // If initialData exists, set the selectedFuelForm and fetch units
        if (initialData?.fuel_id) {
          const initialFuel = response.data.data.find(fuel => fuel.value === initialData.fuel_id);
          if (initialFuel) {
            setSelectedFuelForm(initialFuel.fuel_form);
            getFuelUnit(initialFuel.fuel_form);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const getVehicleType = async () => {
    try{
        const response = await api.get('/admin/getVehicleTypes?status=ACTIVE');
        if(response.data.status == '200'){
            setVehicleType(response.data.data);
        }
    }catch(error){
      console.error(error);
    }
  };

  const getVehicleSubType = async () => {
    try{
        const response = await api.get('/admin/getVehicleSubType?status=ACTIVE');
        if(response.data.status == '200'){
            setVehicleSubType(response.data.data);
            
            // If there's an initial type selected, filter subtypes accordingly
            if (formData.vehicleTypeId) {
                filterVehicleSubTypes(formData.vehicleTypeId);
            }
        }
    }catch(error){
      console.error(error);
    }
  };

  const getFuelUnit = async (fuelForm) => {
    try {
      // Determine which type_code to use based on fuel form
      let typeCode = "";
      if (fuelForm === "LIQUID") {
        typeCode = "LIQUID";
      } else if (fuelForm === "GAS") {
        typeCode = "VOLUME";
      } else if (fuelForm === "MASS") {
        typeCode = "MASS";
      }else {
        typeCode = "LIQUID,MASS,VOLUME"; // Fetch all if no specific form
      }
      
      const res = await api.get(`/common/measurementType?type_code=${typeCode}`);
      setUnits(res.data.data);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const handleFuelChange = (e) => {
    const fuelId = e.target.value;
    
    // Find the selected fuel object
    const selectedFuel = fuel.find(f => f.value === fuelId);
    
    if (selectedFuel) {
      // Update the fuel form state
      setSelectedFuelForm(selectedFuel.fuel_form);
      
      // Update formData with new fuelId and reset unitId
      setFormData(prev => ({
        ...prev,
        fuelId: fuelId,
        unitId: '' // Reset unit selection when fuel changes
      }));
      
      // Clear unit error when fuel changes
      if (errors.unitId) {
        setErrors(prev => ({
          ...prev,
          unitId: ''
        }));
      }
      
      // Fetch appropriate units for the selected fuel form
      getFuelUnit(selectedFuel.fuel_form);
    }
  };

  useEffect(() => {
    getVehicleType();
    getVehicleSubType();
    getFuel();
    // Initial units fetch will happen after getFuel if initialData exists
    if (!initialData?.fuel_id) {
      getFuelUnit();
    }
  }, []);

  // Initialize filtered subtypes based on initial data
  useEffect(() => {
    if (formData.vehicleTypeId && vehicleSubType.length > 0) {
      filterVehicleSubTypes(formData.vehicleTypeId);
    }
  }, [vehicleSubType]);

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
        
        {/* <div className="space-y-2">
            <label htmlFor="vehicleTypeId" className="text-sm font-medium block">Vehicle Type<span className="text-red-500">*</span></label>
            <FormControl fullWidth size="small">
              <Select
                labelId="vehicleTypeId-label"
                id="vehicleTypeId"
                name="vehicleTypeId"
                value={formData.vehicleTypeId}
                onChange={handleInputChange}
                disabled={isSubmitting}
                error={Boolean(errors.vehicleTypeId)}
                className={'w-full'}
              >
                {vehicleType.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.vehicleTypeId && (
              <p className="text-sm text-red-500 mt-1">{errors.vehicleTypeId}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="vehicleSubTypeId" className="text-sm font-medium block">Vehicle Sub Type<span className="text-red-500">*</span></label>
            <FormControl fullWidth size="small">
              <Select
                labelId="vehicleSubTypeId-label"
                id="vehicleSubTypeId"
                name="vehicleSubTypeId"
                value={formData.vehicleSubTypeId}
                onChange={handleInputChange}
                disabled={isSubmitting || !formData.vehicleTypeId || filteredVehicleSubTypes.length === 0}
                error={Boolean(errors.vehicleSubTypeId)}
                className={'w-full'}
              >
                {filteredVehicleSubTypes.map((subType) => (
                  <MenuItem key={subType.id} value={subType.id}>
                    {subType.sub_type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.vehicleSubTypeId && (
              <p className="text-sm text-red-500 mt-1">{errors.vehicleSubTypeId}</p>
            )}
            {formData.vehicleTypeId && filteredVehicleSubTypes.length === 0 && (
              <p className="text-sm text-amber-500 mt-1">No subtypes available for this vehicle type</p>
            )}
          </div> */}

          <div className="space-y-2">
            <label htmlFor="fuelId" className="text-sm font-medium block">Fuel Type<span className="text-red-500">*</span></label>
            <FormControl fullWidth size="small">
              <Select
                labelId="fuelId-label"
                id="fuelId"
                name="fuelId"
                value={formData.fuelId}
                onChange={handleFuelChange}
                disabled={isSubmitting}
                error={Boolean(errors.fuelId)}
                className={'w-full'}
              >
                {fuel.map((gas) => (
                  <MenuItem key={gas.value} value={gas.value}>
                    {gas.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.fuelId && (
              <p className="text-sm text-red-500 mt-1">{errors.fuelId}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="unitId" className="text-sm font-medium block">Fuel Unit<span className="text-red-500">*</span></label>
            <FormControl fullWidth size="small">
              <Select
                labelId="unit-label"
                id="unitId"
                name="unitId"
                value={formData.unitId}
                onChange={handleInputChange}
                disabled={isSubmitting || !formData.fuelId}
                error={Boolean(errors.unitId)}
                className={'w-full'}
              >
                {units.map((unit) => (
                  <MenuItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.unitId && (
              <p className="text-sm text-red-500 mt-1">{errors.unitId}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="emission" className="text-sm font-medium">
            kgCo2e Emission
              <span className="text-red-500">*</span>
            </label>
            <TextField
              size="small"
              id="emission"
              name="emission"
              value={formData.emission}
              onChange={handleInputChange}
              placeholder="Enter Emission Value kgCo2e"
              disabled={isSubmitting}
              error={Boolean(errors.emission)}
              className={'w-full'}
              type="number"
            />
            {errors.emission && (
              <p className="text-sm text-red-500 mt-1">{errors.emission}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="yearPicker" className="text-sm font-medium block">
              Select Year
              <span className="text-red-500">*</span>
            </label>
            <DatePicker
              id="yearPicker"
              selected={formData.year}
              onChange={(date) => handleInputChange({ target: { name: "year", value: date } })}
              showYearPicker
              dateFormat="yyyy"
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              wrapperClassName="w-full"
              disabled={isSubmitting}
            />
            {errors.year && <p className="text-sm text-red-500 mt-1">{errors.year}</p>}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={EmissionFactorId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default AddUpdateVehicleEmissionFactorMaster;